import { useState } from "react";
import { DashboardLayout } from "@/pages/Dashboard";
import { useLanguage } from "@/App";
import { Badge } from "@/components/ui/badge";
import * as Icons from "lucide-react";
import { ExternalLink, ChevronLeft, Copy, CheckCircle } from "lucide-react";
import {
  EDU_SECTIONS, START_CONTENT, SCHOLARSHIPS_CONTENT, ENGLISH_CONTENT,
  CONTACT_CONTENT, LIFE_CONTENT,
} from "@/lib/eduContent";
import CollegesDirectory from "@/components/CollegesDirectory";

const COLOR_MAP = {
  purple:  { tile: "bg-purple-500/15",  icon: "text-purple-300",  ring: "ring-purple-500",  text: "text-purple-300",  glow: "bg-purple-500/15" },
  sky:     { tile: "bg-sky-500/15",     icon: "text-sky-300",     ring: "ring-sky-500",     text: "text-sky-300",     glow: "bg-sky-500/15" },
  amber:   { tile: "bg-amber-500/15",   icon: "text-amber-300",   ring: "ring-amber-500",   text: "text-amber-300",   glow: "bg-amber-500/15" },
  emerald: { tile: "bg-emerald-500/15", icon: "text-emerald-300", ring: "ring-emerald-500", text: "text-emerald-300", glow: "bg-emerald-500/15" },
  rose:    { tile: "bg-rose-500/15",    icon: "text-rose-300",    ring: "ring-rose-500",    text: "text-rose-300",    glow: "bg-rose-500/15" },
  violet:  { tile: "bg-violet-500/15",  icon: "text-violet-300",  ring: "ring-violet-500",  text: "text-violet-300",  glow: "bg-violet-500/15" },
};

export default function EducationSection() {
  const { t, language } = useLanguage();
  const [activeKey, setActiveKey] = useState("start");

  const active = EDU_SECTIONS.find((s) => s.key === activeKey) || EDU_SECTIONS[0];
  const ac = COLOR_MAP[active.color];

  return (
    <DashboardLayout title={t("التعليم والدراسة", "Education & Study")}>
      <div className="space-y-6" data-testid="education-section">
        {/* Hero */}
        <div className="glass-card rounded-3xl p-6 lg:p-8 relative overflow-hidden">
          <div className={`absolute -top-24 -left-24 w-72 h-72 ${ac.glow} blur-[100px] rounded-full`} />
          <div className="relative">
            <Badge className="bg-purple-500/20 text-purple-300 mb-3">{t("قسم مستقل عن دليل المهاجر", "Separate from the Immigrant Guide")}</Badge>
            <h2 className="text-2xl lg:text-3xl font-bold text-white mb-1">
              {t("التعليم والدراسة في أمريكا", "Studying in America")}
            </h2>
            <p className="text-slate-400 text-sm">
              {t(
                "كل ما تحتاجه للتخطيط، التقديم، والحياة الطلابية — مع دليل رسمي للجامعات والكليات.",
                "Everything you need to plan, apply, and live as a student — plus an official directory of colleges & universities.",
              )}
            </p>
          </div>
        </div>

        {/* Section navigation */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="edu-nav">
          {EDU_SECTIONS.map((s) => {
            const Icon = Icons[s.icon] || Icons.BookOpen;
            const cc = COLOR_MAP[s.color];
            const active = s.key === activeKey;
            return (
              <button
                key={s.key}
                onClick={() => setActiveKey(s.key)}
                className={`glass-card rounded-2xl p-3 text-center transition-all hover:scale-105 border ${active ? `ring-2 ${cc.ring} ${cc.tile} border-transparent` : "border-transparent"}`}
                data-testid={`edu-tab-${s.key}`}
              >
                <div className={`w-10 h-10 mx-auto mb-2 rounded-xl flex items-center justify-center ${cc.tile}`}>
                  <Icon className={cc.icon} size={20} />
                </div>
                <p className={`text-xs font-semibold ${active ? cc.text : "text-white"}`}>
                  {language === "ar" ? s.ar : s.en}
                </p>
              </button>
            );
          })}
        </div>

        {/* Active section content */}
        <div>
          {activeKey === "start"        && <ContentSection content={START_CONTENT}        color={COLOR_MAP.purple}  t={t} language={language} />}
          {activeKey === "directory"    && <CollegesDirectory />}
          {activeKey === "scholarships" && <ContentSection content={SCHOLARSHIPS_CONTENT} color={COLOR_MAP.amber}   t={t} language={language} />}
          {activeKey === "english"      && <ContentSection content={ENGLISH_CONTENT}      color={COLOR_MAP.emerald} t={t} language={language} />}
          {activeKey === "contact"      && <ContactSection content={CONTACT_CONTENT}      color={COLOR_MAP.rose}    t={t} language={language} />}
          {activeKey === "life"         && <ContentSection content={LIFE_CONTENT}         color={COLOR_MAP.violet}  t={t} language={language} />}
        </div>
      </div>
    </DashboardLayout>
  );
}

// ─────────── Sub-views ───────────

function ContentSection({ content, color, t, language }) {
  return (
    <div className="space-y-5" data-testid="edu-content-section">
      {content.blocks.map((b, i) => (
        <article key={i} className="glass-card rounded-2xl p-5 lg:p-6 space-y-3">
          <h3 className={`text-lg lg:text-xl font-bold ${color.text}`}>
            {language === "ar" ? b.title_ar : (b.title_en || b.title_ar)}
          </h3>
          <div className="space-y-2 text-slate-300 text-sm lg:text-base leading-relaxed">
            {b.body_ar.map((line, j) => (
              <p key={j} dangerouslySetInnerHTML={{ __html: renderMarkdownLite(line) }} />
            ))}
          </div>
        </article>
      ))}

      {content.resources && content.resources.length > 0 && (
        <div className="glass-card rounded-2xl p-5">
          <h4 className="text-sm font-semibold text-slate-300 mb-3">{t("مصادر رسمية مفيدة", "Official Resources")}</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {content.resources.map((r) => (
              <a
                key={r.url}
                href={r.url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-between gap-2 text-sm text-slate-200 bg-slate-900/60 hover:bg-slate-800 rounded-xl px-3 py-2 transition"
                data-testid={`edu-resource-${r.url}`}
              >
                <span className="inline-flex items-center gap-2"><ExternalLink size={13} className={color.icon} />{r.label}</span>
                <ChevronLeft size={14} className="text-slate-500" />
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ContactSection({ content, color, t, language }) {
  return (
    <div className="space-y-5">
      <ContentSection content={{ blocks: content.blocks }} color={color} t={t} language={language} />

      {content.templates && content.templates.length > 0 && (
        <div className="glass-card rounded-2xl p-5 lg:p-6 space-y-4">
          <h3 className={`text-lg font-bold ${color.text}`}>{t("نماذج بريد إلكتروني جاهزة", "Email Templates")}</h3>
          <div className="space-y-4">
            {content.templates.map((tpl) => (
              <EmailTemplate key={tpl.key} tpl={tpl} language={language} t={t} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function EmailTemplate({ tpl, language, t }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(tpl.body);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* no-op */ }
  };
  return (
    <div className="bg-slate-900/60 rounded-xl p-4" data-testid={`edu-template-${tpl.key}`}>
      <div className="flex items-center justify-between mb-2 gap-3">
        <h4 className="font-semibold text-white text-sm">{language === "ar" ? tpl.title_ar : tpl.title_en}</h4>
        <button
          onClick={copy}
          className="inline-flex items-center gap-1 text-xs text-slate-300 hover:text-white bg-slate-800 hover:bg-slate-700 rounded-lg px-3 py-1.5 transition"
          data-testid={`edu-template-copy-${tpl.key}`}
        >
          {copied ? <><CheckCircle size={12} className="text-emerald-400" />{t("تم النسخ", "Copied")}</>
                  : <><Copy size={12} />{t("نسخ", "Copy")}</>}
        </button>
      </div>
      <pre className="text-xs text-slate-300 whitespace-pre-wrap font-mono leading-relaxed">{tpl.body}</pre>
    </div>
  );
}

// Lightweight **bold** -> <strong> renderer for the static blocks.
function renderMarkdownLite(text) {
  if (!text) return "";
  return String(text).replace(/\*\*(.+?)\*\*/g, '<strong class="text-white">$1</strong>');
}
